// react
import { forwardRef, ReactNode, useEffect } from 'react';
// helmet
import { Helmet } from 'react-helmet-async';
// @mui
import Box from '@mui/material/Box';
// path
import { BASE } from '../routes/paths';
// hooks
import useCookiesPreferences from '../hooks/useCookiesPreferences';
// analytics
import ReactGA from 'react-ga4';

// ----------------------------------------------------------------------

const DESCRIPTION =
  'Join the data-centric revolution with dratio. Analyzing, understanding and improving your decisions through data will never be a challenge again';

export function setVisibility(visibility: boolean) {
  document.documentElement.style.setProperty(
    '--recaptcha-visibility',
    visibility ? 'visible' : 'hidden'
  );
}

interface Props {
  children: ReactNode;
  meta?: ReactNode;
  title: string;
  description?: string;
  image?: string;
  sitename?: string;
  type?: string;
  section?: string;
  tag?: string;
  recaptcha?: boolean;
  ga?: boolean;
  customPathname?: string;
}
const Page = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      title = '',
      description,
      image,
      type,
      section,
      tag,
      sitename,
      meta,
      ga = true,
      recaptcha = false,
      customPathname,
      ...other
    },
    ref
  ) => {
    const { analytics } = useCookiesPreferences();
    useEffect(
      () => {
        setVisibility(recaptcha);
        if (ga && analytics) {
          if (customPathname) {
            ReactGA.send({ hitType: 'pageview', page: customPathname });
          } else {
            ReactGA.send('pageview');
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );
    const location = window.location.pathname;
    const titleTag = `${title} · dratio.io`;
    const canonical = `${BASE}${customPathname || location}`;

    return (
      <>
        <Helmet>
          <title>{titleTag}</title>
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={titleTag} />
          <meta property="og:type" content={type || 'website'} />
          <meta name="description" content={description || DESCRIPTION} />
          <meta property="og:description" content={description || DESCRIPTION} />
          <meta property="og:url" content={canonical} />
          <meta property="article:section" content={section || 'dratio.io'} />
          <meta property="article:tag" content={tag || 'dratio.io'} />
          <meta property="og:image" content={image || 'https://dratio.io/branding/logo.jpg'} />
          <meta
            property="og:image:secure_url"
            content={image || 'https://dratio.io/branding/logo.jpg'}
          />
          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:image" content={image || 'https://dratio.io/branding/logo.jpg'} />

          {meta}
        </Helmet>

        <Box ref={ref} {...other}>
          {children}
        </Box>
      </>
    );
  }
);

export default Page;
